"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = compareUpgradeablePackage;
function containsInclusion(inclusions, searchTerms) {
    const normalizedSearchTerms = searchTerms.map(term => term.toLowerCase());
    return inclusions.some(inclusion => {
        if (!inclusion.inclusion)
            return false;
        const { description, name, type } = inclusion.inclusion;
        const normalizedDescription = description?.toLowerCase() || '';
        const normalizedName = name?.toLowerCase() || '';
        const normalizedType = type?.toLowerCase() || '';
        return normalizedSearchTerms.some(searchTerm => {
            const matchesDescription = normalizedDescription.includes(searchTerm);
            const matchesName = normalizedName.includes(searchTerm);
            const matchesType = normalizedType.includes(searchTerm);
            return matchesDescription || matchesName || matchesType;
        });
    });
}
/**
 * Checks if the upgrade option has at least the same cancellation policies as the selected package.
 * The upgrade option can have additional policies, but must include all policies from the selected package.
 *
 * @param selectedPolicies - Cancellation policies from the currently selected package
 * @param optionPolicies - Cancellation policies from the upgrade option
 * @returns `true` if the upgrade option has at least the same policies as the selected package
 */
function hasAtLeastSameCancellationPolicies(selectedPolicies, optionPolicies) {
    // If selected package has no policies, any upgrade option is acceptable
    if (!selectedPolicies || selectedPolicies.length === 0) {
        return true;
    }
    // If upgrade option has no policies but selected package does, it's not acceptable
    if (!optionPolicies || optionPolicies.length === 0) {
        return false;
    }
    // Check if all selected package policies are present in the upgrade option
    return selectedPolicies.every(selectedPolicy => {
        // Skip policies without a policyCode
        if (!selectedPolicy.policyCode)
            return true;
        return optionPolicies.some(optionPolicy => optionPolicy.policyCode === selectedPolicy.policyCode);
    });
}
/**
 * Determines if a package option qualifies as an upgrade compared to the currently selected package.
 *
 * A package option is considered upgradeable if it meets ALL of the following criteria:
 *
 * 1. **Same cancellation policy**: Must have the same cancellation policy code as the selected package
 * 2. **Higher price**: The upgrade option must have a higher hotel price than the selected package
 * 3. **Equal or more inclusions**: The upgrade option must have at least as many inclusions as the selected package
 * 4. **Transfer inclusion matching**: If the selected package includes transfer/transport, the upgrade option must also include it
 * 5. **Food inclusion matching**: If the selected package includes food/meal, the upgrade option must also include it
 *
 * @param option - The package option being evaluated as a potential upgrade
 * @param selectedPackageOption - The currently selected package option to compare against
 * @returns `true` if the option qualifies as an upgrade, `false` otherwise
 */
function compareUpgradeablePackage(option, selectedPackageOption) {
    // Extract inclusions from selected package and upgradeable package option
    const selectedInclusions = selectedPackageOption.package.inclusionsV2 || [];
    const optionInclusions = option.package.inclusionsV2 || [];
    // Verify matched inclusions
    const selectedHasTransfer = containsInclusion(selectedInclusions, ['transfer', 'transport']);
    const optionHasTransfer = containsInclusion(optionInclusions, ['transfer', 'transport']);
    const selectedHasFood = containsInclusion(selectedInclusions, ['food', 'meal']);
    const optionHasFood = containsInclusion(optionInclusions, ['food', 'meal']);
    // Check if upgrade option has at least the same cancellation policies as selected package
    const selectedCancellationPolicies = selectedPackageOption.package.roomRate?.cancellationPolicies;
    const optionCancellationPolicies = option.package.roomRate?.cancellationPolicies;
    const hasCompatibleCancellationPolicies = hasAtLeastSameCancellationPolicies(selectedCancellationPolicies, optionCancellationPolicies);
    return (hasCompatibleCancellationPolicies &&
        option.hotelPrice > selectedPackageOption.hotelPrice &&
        optionInclusions.length >= selectedInclusions.length &&
        // must have transfer inclusion if selected package has it
        (!selectedHasTransfer || optionHasTransfer) &&
        // must have food inclusion if selected package has it
        (!selectedHasFood || optionHasFood));
}
